import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { base_url } from "../../constants";
import "./questionBank.css";

const QuestionBank = () => {
  document.title = "Unit Questions | Medicotoon";
  const location = useLocation();

  const localdata = localStorage.getItem("elmatary_admin");
  const adminData = localdata ? JSON.parse(localdata) : {};

  // Filters State
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loadingCourses, setLoadingCourses] = useState(false);

  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loadingUnits, setLoadingUnits] = useState(false);

  // Questions State & Server Pagination
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginationInfo, setPaginationInfo] = useState({
    total_records: 0,
    total_pages: 1,
    current_page: 1,
    limit: 10,
  });

  // Add Question Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addQuestionData, setAddQuestionData] = useState({
    question_text: "",
    question_image_url: "",
    help_text: "",
    help_pdf: "",
    help_video: "",
    pdf_page: "",
    video_time: "",
    explain: "",
  });
  const [addAnswersList, setAddAnswersList] = useState([
    { id: 1, answer: "", answer_exp: "", checked: true },
    { id: 2, answer: "", answer_exp: "", checked: false },
  ]);
  const [addImageFile, setAddImageFile] = useState(null);
  const [uploadingAddImg, setUploadingAddImg] = useState(false);
  const [submittingAdd, setSubmittingAdd] = useState(false);

  // Modal Mode: 'new' | 'choose_pool' | 'import_unit'
  const [addModalTab, setAddModalTab] = useState("new");
  const [poolQuestions, setPoolQuestions] = useState([]);
  const [loadingPool, setLoadingPool] = useState(false);
  const [poolSearch, setPoolSearch] = useState("");
  const [selectedPoolQuestionIds, setSelectedPoolQuestionIds] = useState([]);
  const [assigningFromPool, setAssigningFromPool] = useState(false);

  // Import from existing Unit states
  const [universitiesList, setUniversitiesList] = useState([]);
  const [loadingUniversities, setLoadingUniversities] = useState(false);
  const [importUniversity, setImportUniversity] = useState(null);
  const [importGradesList, setImportGradesList] = useState([]);
  const [importGrade, setImportGrade] = useState(null);
  const [importCourse, setImportCourse] = useState(null);
  const [importUnit, setImportUnit] = useState(null);
  const [importUnitsList, setImportUnitsList] = useState([]);
  const [loadingImportUnits, setLoadingImportUnits] = useState(false);
  const [importUnitQuestions, setImportUnitQuestions] = useState([]);
  const [loadingImportQuestions, setLoadingImportQuestions] = useState(false);
  const [selectedImportQuestionIds, setSelectedImportQuestionIds] = useState([]);
  const [importingFromUnit, setImportingFromUnit] = useState(false);

  // Import from Question Bank states
  const [questionBanksList, setQuestionBanksList] = useState([]);
  const [loadingQuestionBanks, setLoadingQuestionBanks] = useState(false);
  const [selectedImportBank, setSelectedImportBank] = useState(null);
  const [importBankQuestions, setImportBankQuestions] = useState([]);
  const [loadingImportBankQuestions, setLoadingImportBankQuestions] = useState(false);
  const [selectedImportBankQuestionIds, setSelectedImportBankQuestionIds] = useState([]);
  const [importingFromBank, setImportingFromBank] = useState(false);

  // Filtered import courses based on selected University/Grade
  const filteredImportCourses = useMemo(() => {
    return courses.filter((c) => {
      const orig = c.original || {};
      if (importUniversity && String(orig.university_id) !== String(importUniversity.value)) {
        return false;
      }
      if (importGrade && String(orig.grade_id) !== String(importGrade.value)) {
        return false;
      }
      return true;
    });
  }, [courses, importUniversity, importGrade]);

  // Fetch Universities for Hierarchy
  const getUniversities = async () => {
    setLoadingUniversities(true);
    try {
      const res = await axios.post(
        `${base_url}/admin/universities/select_universities_grade.php`,
        {
          admin_id: adminData.admin_id,
          access_token: adminData.access_token,
        }
      );
      if (res && res.status === "success" && Array.isArray(res.message)) {
        setUniversitiesList(
          res.message.map((u) => ({
            value: u.university_id,
            label: u.university_name,
            original: u,
          }))
        );
      }
    } catch (err) {
      console.error("Error loading universities", err);
    } finally {
      setLoadingUniversities(false);
    }
  };

  // Edit Question Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editQuestionData, setEditQuestionData] = useState({});
  const [editAnswersList, setEditAnswersList] = useState([]);
  const [editImageFile, setEditImageFile] = useState(null);
  const [uploadingEditImg, setUploadingEditImg] = useState(false);
  const [submittingEdit, setSubmittingEdit] = useState(false);

  // Status/Confirm State
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState({});

  // 1. Fetch Units when Course changes
  const getUnits = async (courseId) => {
    if (!courseId) {
      setUnits([]);
      setSelectedUnit(null);
      return;
    }
    setLoadingUnits(true);
    setUnits([]);
    setSelectedUnit(null);
    setQuestions([]);
    try {
      const send_data = {
        admin_id: adminData.admin_id,
        access_token: adminData.access_token,
        course_id: courseId,
      };
      const res = await axios.post(
        `${base_url}/admin/courses/select_course_units.php`,
        send_data
      );
      if (res.status === "success" && Array.isArray(res.message)) {
        setUnits(
          res.message.map((u) => ({
            value: u.unit_id,
            label: u.unit_name,
            original: u,
          }))
        );
      }
    } catch (err) {
      console.error("Error fetching units", err);
      toast.error("Failed to load units");
    } finally {
      setLoadingUnits(false);
    }
  };

  // 2. Fetch Questions when Unit changes with Server-side Pagination
  const getQuestions = async (unitId, courseId, page = currentPage, limit = pageSize, search = searchTerm) => {
    const cId = courseId || selectedCourse?.value;
    const uId = unitId || selectedUnit?.value;
    if (!uId) {
      setQuestions([]);
      return;
    }
    setLoadingQuestions(true);
    try {
      const data_send = {
        unit_id: uId,
        course_id: cId,
        page: page,
        limit: limit,
        search: search,
      };
      const res = await axios.post(
        `${base_url}/admin/Exams/select_questions.php`,
        JSON.stringify(data_send)
      );
      if (res && res.message && Array.isArray(res.message)) {
        setQuestions(res.message);
        if (res.pagination) {
          setPaginationInfo(res.pagination);
        } else {
          setPaginationInfo({
            total_records: res.message.length,
            total_pages: Math.ceil(res.message.length / limit) || 1,
            current_page: page,
            limit: limit,
          });
        }
      } else {
        setQuestions([]);
        setPaginationInfo({
          total_records: 0,
          total_pages: 1,
          current_page: 1,
          limit: limit,
        });
      }
    } catch (err) {
      console.error("Error fetching questions", err);
      toast.error("Failed to load questions");
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoadingCourses(true);
      try {
        const data_send = {
          admin_id: adminData.admin_id,
          access_token: adminData.access_token,
        };
        const res = await axios.post(
          `${base_url}/admin/courses/select_courses.php`,
          JSON.stringify(data_send)
        );
        if (res.status === "success" && Array.isArray(res.message)) {
          const courseOptions = res.message.map((c) => ({
            value: c.course_id,
            label: c.course_name,
            original: c,
          }));
          setCourses(courseOptions);

          // If navigated with initial state
          if (location?.state?.course_id || location?.state?.coursedata?.course_id) {
            const targetCourseId = location?.state?.course_id || location?.state?.coursedata?.course_id;
            const foundCourse = courseOptions.find((c) => String(c.value) === String(targetCourseId));
            if (foundCourse) {
              setSelectedCourse(foundCourse);

              // Load units for this course
              setLoadingUnits(true);
              const unitRes = await axios.post(
                `${base_url}/admin/courses/select_course_units.php`,
                {
                  admin_id: adminData.admin_id,
                  access_token: adminData.access_token,
                  course_id: targetCourseId,
                }
              );
              if (unitRes.status === "success" && Array.isArray(unitRes.message)) {
                const unitOptions = unitRes.message.map((u) => ({
                  value: u.unit_id,
                  label: u.unit_name,
                  original: u,
                }));
                setUnits(unitOptions);

                const targetUnitId = location?.state?.unit_id || location?.state?.unitdata?.unit_id || location?.state?.unitData?.unit_id;
                if (targetUnitId) {
                  const foundUnit = unitOptions.find((u) => String(u.value) === String(targetUnitId));
                  if (foundUnit) {
                    setSelectedUnit(foundUnit);
                    getQuestions(targetUnitId, targetCourseId);
                  }
                }
              }
              setLoadingUnits(false);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching courses", err);
        toast.error("Failed to load courses");
      } finally {
        setLoadingCourses(false);
      }
    };

    init();
  }, [location?.state]);

  // Search debounce
  useEffect(() => {
    if (selectedUnit && selectedCourse) {
      const delayDebounce = setTimeout(() => {
        setCurrentPage(1);
        getQuestions(selectedUnit.value, selectedCourse.value, 1, pageSize, searchTerm);
      }, 400);
      return () => clearTimeout(delayDebounce);
    }
  }, [searchTerm]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (paginationInfo.total_pages || 1)) {
      setCurrentPage(newPage);
      getQuestions(selectedUnit?.value, selectedCourse?.value, newPage, pageSize, searchTerm);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    if (selectedUnit && selectedCourse) {
      getQuestions(selectedUnit.value, selectedCourse.value, 1, newSize, searchTerm);
    }
  };

  // Handle Upload Image
  const handleUploadImage = async (file, isEdit = false) => {
    if (!file) return;
    const formdata = new FormData();
    formdata.append("image", file);
    if (isEdit) setUploadingEditImg(true);
    else setUploadingAddImg(true);

    try {
      const res = await axios.post(
        `${base_url}/admin/image_uploader.php`,
        formdata
      );
      if (res) {
        if (isEdit) {
          setEditQuestionData((prev) => ({
            ...prev,
            question_image_url: res,
          }));
        } else {
          setAddQuestionData((prev) => ({
            ...prev,
            question_image_url: res,
          }));
        }
        toast.success("Image uploaded successfully");
      }
    } catch (err) {
      console.error("Error uploading image", err);
      toast.error("Error uploading image");
    } finally {
      if (isEdit) setUploadingEditImg(false);
      else setUploadingAddImg(false);
    }
  };

  // Add Question Handlers
  const handleAddAnswerChange = (index, field, value) => {
    const list = [...addAnswersList];
    list[index][field] = value;
    setAddAnswersList(list);
  };

  const handleAddAnswerSelect = (index) => {
    const list = addAnswersList.map((item, idx) => ({
      ...item,
      checked: idx === index,
    }));
    setAddAnswersList(list);
  };

  const handleAddQuestionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCourse || !selectedUnit) {
      toast.warn("Please select course and unit first");
      return;
    }
    if (!addQuestionData.question_text.trim()) {
      toast.warn("Please enter question title");
      return;
    }
    const validChecked = addAnswersList.find((a) => a.checked);
    if (!validChecked || !validChecked.answer.trim()) {
      toast.warn("Please select and write the valid correct answer");
      return;
    }

    setSubmittingAdd(true);
    try {
      const data_send = {
        unit_id: selectedUnit.value,
        course_id: selectedCourse.value,
        exam_id: "0",
        question_text: addQuestionData.question_text,
        question_answers: addAnswersList
          ?.map((item) => `${item?.answer}/**exp**/${item?.answer_exp || ""}`)
          ?.join("//CAMP//"),
        question_valid_answer: validChecked.answer,
        question_image: addQuestionData.question_image_url?.length
          ? addQuestionData.question_image_url
          : null,
        help_text: addQuestionData.help_text || "",
        help_pdf: addQuestionData.help_pdf || "",
        help_video: addQuestionData.help_video || "",
        pdf_page: addQuestionData.pdf_page || "",
        video_time: addQuestionData.video_time || "",
        explain: addQuestionData.explain || "",
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/add_ques.php`,
        JSON.stringify(data_send)
      );

      if (res.status === "success") {
        toast.success("Question added successfully");
        setIsAddModalOpen(false);
        setAddQuestionData({
          question_text: "",
          question_image_url: "",
          help_text: "",
          help_pdf: "",
          help_video: "",
          pdf_page: "",
          video_time: "",
          explain: "",
        });
        setAddAnswersList([
          { id: 1, answer: "", answer_exp: "", checked: true },
          { id: 2, answer: "", answer_exp: "", checked: false },
        ]);
        setAddImageFile(null);
        getQuestions(selectedUnit.value, selectedCourse.value);
      } else {
        toast.error(res.message || "Failed to add question");
      }
    } catch (err) {
      console.error("Error adding question", err);
      toast.error("Server connection error");
    } finally {
      setSubmittingAdd(false);
    }
  };

  // Fetch Pool questions (Standalone/original questions where referenced_from is NULL/0)
  const getPoolQuestions = async (search = poolSearch) => {
    setLoadingPool(true);
    try {
      const data_send = {
        unit_id: "0",
        course_id: "0",
        page: 1,
        limit: 200,
        search: search,
      };
      const res = await axios.post(
        `${base_url}/admin/Exams/select_questions.php`,
        JSON.stringify(data_send)
      );
      if (res && res.message && Array.isArray(res.message)) {
        setPoolQuestions(res.message);
      } else {
        setPoolQuestions([]);
      }
    } catch (err) {
      console.error("Error fetching pool questions", err);
      toast.error("Failed to load questions pool");
    } finally {
      setLoadingPool(false);
    }
  };

  // Toggle selection for a question from pool
  const togglePoolQuestionSelection = (questionId) => {
    setSelectedPoolQuestionIds((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  // Select all or deselect all available pool questions
  const toggleSelectAllPool = (availableQuestions) => {
    if (selectedPoolQuestionIds.length === availableQuestions.length && availableQuestions.length > 0) {
      setSelectedPoolQuestionIds([]);
    } else {
      setSelectedPoolQuestionIds(availableQuestions.map((q) => q.question_id));
    }
  };

  // Assign multiple selected questions from pool to current Unit & Course
  const handleAssignFromPool = async () => {
    if (selectedPoolQuestionIds.length === 0) {
      toast.warn("Please select at least one question");
      return;
    }
    if (!selectedCourse || !selectedUnit) {
      toast.warn("Course and unit must be selected");
      return;
    }

    setAssigningFromPool(true);
    try {
      const questionsToAssign = poolQuestions
        .filter((q) => selectedPoolQuestionIds.includes(q.question_id))
        .map((q) => {
          const validChecked = Array.isArray(q.question_answers)
            ? q.question_answers.find((a) => a.answer_check)
            : null;
          return {
            referenced_from: q.referenced_from || q.question_id,
            unit_id: selectedUnit.value,
            course_id: selectedCourse.value,
            exam_id: "0",
            question_text: q.question_text,
            question_answers: Array.isArray(q.question_answers)
              ? q.question_answers
                .map((item) => `${item?.answer_text || item?.answer || ""}/**exp**/${item?.answer_exp || ""}`)
                .join("//CAMP//")
              : (q.question_answers || ""),
            question_valid_answer: validChecked
              ? (validChecked.answer_text || validChecked.answer || "")
              : (q.question_valid_answer || ""),
            question_image: q.question_image || null,
            help_text: q.help_text || "",
            help_pdf: q.help_pdf || "",
            help_video: q.help_video || "",
            pdf_page: q.pdf_page || "",
            video_time: q.video_time || "",
            explain: q.explain || "",
          };
        });

      const data_send = {
        course_id: selectedCourse.value,
        unit_id: selectedUnit.value,
        questions: questionsToAssign,
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/add_ques.php`,
        JSON.stringify(data_send)
      );

      if (res && res.status === "success") {
        toast.success(`Successfully added ${questionsToAssign.length} question(s) to ${selectedUnit.label}!`);
        setIsAddModalOpen(false);
        setSelectedPoolQuestionIds([]);
        setCurrentPage(1);
        getQuestions(selectedUnit.value, selectedCourse.value, 1, pageSize, searchTerm);
      } else {
        toast.error(res?.message || "Failed to add questions");
      }
    } catch (err) {
      console.error("Error assigning from pool", err);
      toast.error("An error occurred during assignment");
    } finally {
      setAssigningFromPool(false);
    }
  };

  // Fetch Units when Import Course changes
  const getImportUnits = async (courseId) => {
    if (!courseId) {
      setImportUnitsList([]);
      setImportUnit(null);
      setImportUnitQuestions([]);
      return;
    }
    setLoadingImportUnits(true);
    setImportUnitsList([]);
    setImportUnit(null);
    setImportUnitQuestions([]);
    setSelectedImportQuestionIds([]);
    try {
      const res = await axios.post(`${base_url}/admin/courses/select_course_units.php`, {
        admin_id: adminData.admin_id,
        access_token: adminData.access_token,
        course_id: courseId,
      });
      if (res.status === "success" && Array.isArray(res.message)) {
        // Filter out current unit from source
        const filtered = res.message
          .filter((u) => String(u.unit_id) !== String(selectedUnit?.value))
          .map((u) => ({
            value: u.unit_id,
            label: u.unit_name,
          }));
        setImportUnitsList(filtered);
      }
    } catch (err) {
      console.error("Error fetching import units", err);
      toast.error("Failed to load units");
    } finally {
      setLoadingImportUnits(false);
    }
  };

  // Fetch questions from another Unit for importing
  const getImportUnitQuestions = async (unitId, courseId) => {
    if (!unitId || !courseId) {
      setImportUnitQuestions([]);
      return;
    }
    setLoadingImportQuestions(true);
    setSelectedImportQuestionIds([]);
    try {
      const res = await axios.post(
        `${base_url}/admin/Exams/select_questions.php`,
        JSON.stringify({
          unit_id: unitId,
          course_id: courseId,
          page: 1,
          limit: 300000,
        })
      );
      if (res && res.message && Array.isArray(res.message)) {
        setImportUnitQuestions(res.message);
        // By default select all questions in that unit for fast full-unit import
        setSelectedImportQuestionIds(res.message.map((q) => q.question_id));
      } else {
        setImportUnitQuestions([]);
      }
    } catch (err) {
      console.error("Error fetching unit questions", err);
      toast.error("Failed to load questions from unit");
    } finally {
      setLoadingImportQuestions(false);
    }
  };

  // Toggle selection for questions from another Unit
  const toggleImportQuestionSelection = (qId) => {
    setSelectedImportQuestionIds((prev) =>
      prev.includes(qId) ? prev.filter((id) => id !== qId) : [...prev, qId]
    );
  };

  // Toggle select all questions from another unit
  const toggleSelectAllImport = (availableQuestions) => {
    if (selectedImportQuestionIds.length === availableQuestions.length && availableQuestions.length > 0) {
      setSelectedImportQuestionIds([]);
    } else {
      setSelectedImportQuestionIds(availableQuestions.map((q) => q.question_id));
    }
  };

  // Import questions from another Unit (keeps original reference!)
  const handleImportFromUnit = async () => {
    if (selectedImportQuestionIds.length === 0) {
      toast.warn("Please select at least one question to import");
      return;
    }
    if (!selectedCourse || !selectedUnit) {
      toast.warn("Target Course and Unit must be selected");
      return;
    }

    setImportingFromUnit(true);
    try {
      const questionsToImport = importUnitQuestions
        .filter((q) => selectedImportQuestionIds.includes(q.question_id))
        .map((q) => {
          const validChecked = Array.isArray(q.question_answers)
            ? q.question_answers.find((a) => a.answer_check)
            : null;
          // Always take the true root reference!
          const trueRefId = q.referenced_from || q.question_id;

          return {
            referenced_from: trueRefId,
            unit_id: selectedUnit.value,
            course_id: selectedCourse.value,
            exam_id: "0",
            question_text: q.question_text,
            question_answers: Array.isArray(q.question_answers)
              ? q.question_answers
                .map((item) => `${item?.answer_text || item?.answer || ""}/**exp**/${item?.answer_exp || ""}`)
                .join("//CAMP//")
              : (q.question_answers || ""),
            question_valid_answer: validChecked
              ? (validChecked.answer_text || validChecked.answer || "")
              : (q.question_valid_answer || ""),
            question_image: q.question_image || null,
            help_text: q.help_text || "",
            help_pdf: q.help_pdf || "",
            help_video: q.help_video || "",
            pdf_page: q.pdf_page || "",
            video_time: q.video_time || "",
            explain: q.explain || "",
          };
        });

      const data_send = {
        course_id: selectedCourse.value,
        unit_id: selectedUnit.value,
        questions: questionsToImport,
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/add_ques.php`,
        JSON.stringify(data_send)
      );

      if (res && res.status === "success") {
        toast.success(`Successfully imported ${questionsToImport.length} question(s) into ${selectedUnit.label}!`);
        setIsAddModalOpen(false);
        setSelectedImportQuestionIds([]);
        setCurrentPage(1);
        getQuestions(selectedUnit.value, selectedCourse.value, 1, pageSize, searchTerm);
      } else {
        toast.error(res?.message || "Failed to import questions");
      }
    } catch (err) {
      console.error("Error importing questions from unit", err);
      toast.error("Failed to import questions");
    } finally {
      setImportingFromUnit(false);
    }
  };

  // Fetch all Question Banks for importing
  const getQuestionBanksList = async () => {
    setLoadingQuestionBanks(true);
    try {
      const data_send = {
        admin_id: adminData?.admin_id,
        access_token: adminData?.access_token,
      };
      const res = await axios.post(
        `${base_url}/admin/question_bank/select_question_banks.php`,
        JSON.stringify(data_send)
      );
      if (res && res.status === "success" && Array.isArray(res.message)) {
        setQuestionBanksList(
          res.message.map((b) => ({
            value: b.bank_id,
            label: b.bank_name || `Bank #${b.bank_id}`,
            original: b,
          }))
        );
      } else {
        setQuestionBanksList([]);
      }
    } catch (err) {
      console.error("Error fetching question banks list", err);
      toast.error("Failed to load question banks");
      setQuestionBanksList([]);
    } finally {
      setLoadingQuestionBanks(false);
    }
  };

  // Fetch questions from a selected Question Bank
  const getImportBankQuestions = async (bankId) => {
    if (!bankId) {
      setImportBankQuestions([]);
      return;
    }
    setLoadingImportBankQuestions(true);
    setSelectedImportBankQuestionIds([]);
    try {
      const res = await axios.post(
        `${base_url}/admin/Exams/select_questions.php`,
        JSON.stringify({
          bank_question_id: bankId,
          page: 1,
          limit: 300000,
        })
      );
      if (res && res.message && Array.isArray(res.message)) {
        setImportBankQuestions(res.message);
        setSelectedImportBankQuestionIds([]);
      } else {
        setImportBankQuestions([]);
      }
    } catch (err) {
      console.error("Error fetching bank questions", err);
      toast.error("Failed to load questions from bank");
      setImportBankQuestions([]);
    } finally {
      setLoadingImportBankQuestions(false);
    }
  };

  // Toggle selection for questions from Question Bank
  const toggleImportBankQuestionSelection = (qId) => {
    setSelectedImportBankQuestionIds((prev) =>
      prev.includes(qId) ? prev.filter((id) => id !== qId) : [...prev, qId]
    );
  };

  // Toggle select all questions from Question Bank
  const toggleSelectAllBankImport = (availableQuestions) => {
    if (selectedImportBankQuestionIds.length === availableQuestions.length && availableQuestions.length > 0) {
      setSelectedImportBankQuestionIds([]);
    } else {
      setSelectedImportBankQuestionIds(availableQuestions.map((q) => q.question_id));
    }
  };

  // Import selected questions from Question Bank into current Unit
  const handleImportFromBank = async () => {
    if (selectedImportBankQuestionIds.length === 0) {
      toast.warn("Please select at least one question to import");
      return;
    }
    if (!selectedCourse || !selectedUnit) {
      toast.warn("Target Course and Unit must be selected");
      return;
    }

    setImportingFromBank(true);
    try {
      const questionsToImport = importBankQuestions
        .filter((q) => selectedImportBankQuestionIds.includes(q.question_id))
        .map((q) => {
          const validChecked = Array.isArray(q.question_answers)
            ? q.question_answers.find((a) => a.answer_check)
            : null;
          const trueRefId = q.referenced_from || q.question_id;

          return {
            referenced_from: trueRefId,
            unit_id: selectedUnit.value,
            course_id: selectedCourse.value,
            bank_question_id: "0",
            exam_id: "0",
            question_text: q.question_text,
            question_answers: Array.isArray(q.question_answers)
              ? q.question_answers
                .map((item) => `${item?.answer_text || item?.answer || ""}/**exp**/${item?.answer_exp || ""}`)
                .join("//CAMP//")
              : (q.question_answers || ""),
            question_valid_answer: validChecked
              ? (validChecked.answer_text || validChecked.answer || "")
              : (q.question_valid_answer || ""),
            question_image: q.question_image || null,
            help_text: q.help_text || "",
            help_pdf: q.help_pdf || "",
            help_video: q.help_video || "",
            pdf_page: q.pdf_page || "",
            video_time: q.video_time || "",
            explain: q.explain || "",
          };
        });

      const data_send = {
        course_id: selectedCourse.value,
        unit_id: selectedUnit.value,
        bank_question_id: "0",
        questions: questionsToImport,
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/add_ques.php`,
        JSON.stringify(data_send)
      );

      if (res && res.status === "success") {
        toast.success(`Successfully imported ${questionsToImport.length} question(s) from Question Bank into ${selectedUnit.label}!`);
        setIsAddModalOpen(false);
        setSelectedImportBankQuestionIds([]);
        setCurrentPage(1);
        getQuestions(selectedUnit.value, selectedCourse.value, 1, pageSize, searchTerm);
      } else {
        toast.error(res?.message || "Failed to import questions");
      }
    } catch (err) {
      console.error("Error importing questions from bank", err);
      toast.error("Failed to import questions from question bank");
    } finally {
      setImportingFromBank(false);
    }
  };

  // Edit Question Handlers
  const handleOpenEdit = (item) => {
    setEditQuestionData({
      ...item,
      question_image_url: item.question_image || "",
    });
    const parsedAnswers = (item.question_answers || []).map((ans, idx) => ({
      id: idx + 1,
      answer: ans.answer_text || "",
      answer_exp: ans.answer_exp || "",
      checked: !!ans.answer_check,
    }));
    setEditAnswersList(
      parsedAnswers.length
        ? parsedAnswers
        : [
          { id: 1, answer: "", answer_exp: "", checked: true },
          { id: 2, answer: "", answer_exp: "", checked: false },
        ]
    );
    setIsEditModalOpen(true);
  };

  const handleEditAnswerChange = (index, field, value) => {
    const list = [...editAnswersList];
    list[index][field] = value;
    setEditAnswersList(list);
  };

  const handleEditAnswerSelect = (index) => {
    const list = editAnswersList.map((item, idx) => ({
      ...item,
      checked: idx === index,
    }));
    setEditAnswersList(list);
  };

  const handleEditQuestionSubmit = async (e) => {
    e.preventDefault();
    const validChecked = editAnswersList.find((a) => a.checked);
    if (!validChecked || !validChecked.answer.trim()) {
      toast.warn("Please select the valid correct answer");
      return;
    }

    setSubmittingEdit(true);
    try {
      const data_send = {
        question_id: editQuestionData.question_id,
        unit_id: editQuestionData.unit_id || selectedUnit?.value,
        course_id: editQuestionData.course_id || selectedCourse?.value,
        exam_id: editQuestionData.exam_id || "0",
        question_text: editQuestionData.question_text,
        question_answers: editAnswersList
          ?.map((item) => `${item?.answer}/**exp**/${item?.answer_exp || ""}`)
          ?.join("//CAMP//"),
        question_valid_answer: validChecked.answer,
        question_image: editQuestionData.question_image_url || "",
        help_text: editQuestionData.help_text || "",
        help_pdf: editQuestionData.help_pdf || "",
        help_video: editQuestionData.help_video || "",
        pdf_page: editQuestionData.pdf_page || "",
        video_time: editQuestionData.video_time || "",
        explain: editQuestionData.explain || "",
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/edit_ques.php`,
        JSON.stringify(data_send)
      );

      if (res.status === "success") {
        toast.success("Question updated successfully");
        setIsEditModalOpen(false);
        getQuestions(selectedUnit?.value, selectedCourse?.value);
      } else {
        toast.error(res.message || "Failed to update question");
      }
    } catch (err) {
      console.error("Error editing question", err);
      toast.error("Server connection error");
    } finally {
      setSubmittingEdit(false);
    }
  };

  // Bulk Delete States for current Unit questions table
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);

  // Toggle single question selection in current unit table
  const toggleSelectQuestion = (qId) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(qId) ? prev.filter((id) => id !== qId) : [...prev, qId]
    );
  };

  // Toggle select all on current unit page
  const toggleSelectAllPage = () => {
    if (selectedQuestionIds.length === questions.length && questions.length > 0) {
      setSelectedQuestionIds([]);
    } else {
      setSelectedQuestionIds(questions.map((q) => q.question_id));
    }
  };

  // Bulk Delete Handler
  const handleBulkDelete = async () => {
    if (selectedQuestionIds.length === 0) {
      toast.warn("Please select at least one question to delete");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedQuestionIds.length} question(s)?`
      )
    ) {
      setIsDeletingBulk(true);
      try {
        const promises = selectedQuestionIds.map((id) =>
          axios.post(`${base_url}/admin/Exams/delete_question.php`, {
            question_id: id,
          })
        );
        await Promise.all(promises);
        toast.success(`Successfully deleted ${selectedQuestionIds.length} question(s)`);
        setSelectedQuestionIds([]);
        getQuestions(selectedUnit?.value, selectedCourse?.value, currentPage, pageSize, searchTerm);
      } catch (err) {
        console.error("Error bulk deleting questions", err);
        toast.error("An error occurred during bulk deletion");
      } finally {
        setIsDeletingBulk(false);
      }
    }
  };

  // Delete Question Handler
  const handleDeleteQuestion = async (questionId) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      try {
        await axios.post(
          `${base_url}/admin/Exams/delete_question.php`,
          {
            question_id: questionId,
          }
        );
        toast.success("Question deleted successfully");
        setSelectedQuestionIds((prev) => prev.filter((id) => id !== questionId));
        getQuestions(selectedUnit?.value, selectedCourse?.value);
      } catch (err) {
        console.error("Error deleting question", err);
        toast.error("Failed to delete question");
      }
    }
  };



  // Toggle Visibility Handler
  const handleToggleHide = async (item) => {
    try {
      const nextHidden = item.hidden === "no" ? "yes" : "no";
      const send_data = {
        admin_id: adminData.admin_id,
        access_token: adminData.access_token,
        question_id: item.question_id,
        hidden: nextHidden,
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/edit_ques.php`,
        JSON.stringify(send_data)
      );

      if (res.status === "success") {
        toast.success(res.message || "Visibility updated successfully");
        getQuestions(selectedUnit?.value, selectedCourse?.value);
      } else {
        toast.error(res.message || "Failed to update visibility");
      }
    } catch (err) {
      console.error("Error updating hidden status", err);
      toast.error("Error updating question status");
    }
  };

  // Table Columns
  const columns = useMemo(
    () => [
      {
        Header: "#",
        Cell: (cellProps) => <b>{cellProps.cell.row.index + 1}</b>,
      },
      {
        Header: "Question ID",
        accessor: "question_id",
      },
      {
        Header: "Question Title",
        accessor: "question_text",
        Cell: ({ value }) => (
          <div
            style={{
              maxWidth: "320px",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {value}
          </div>
        ),
      },
      {
        Header: "Image",
        Cell: ({ row }) => {
          const img = row.original.question_image;
          return img ? (
            <img
              src={img}
              alt="question"
              style={{
                width: "55px",
                height: "55px",
                objectFit: "cover",
                borderRadius: "6px",
                border: "1px solid #eee",
              }}
            />
          ) : (
            <span className="text-muted font-size-12">No Image</span>
          );
        },
      },
      {
        Header: "Answers",
        Cell: ({ row }) => {
          const answers = row.original.question_answers || [];
          return (
            <ul
              style={{
                listStyleType: "none",
                padding: 0,
                margin: 0,
                textAlign: "left",
              }}
            >
              {answers.map((ans, idx) => (
                <li
                  key={idx}
                  style={{
                    color: ans.answer_check ? "#28a745" : "#dc3545",
                    fontWeight: ans.answer_check ? "600" : "normal",
                    marginBottom: "3px",
                  }}
                >
                  {ans.answer_check ? "✓ " : "• "}
                  {ans.answer_text}
                </li>
              ))}
            </ul>
          );
        },
      },
      {
        Header: "Actions",
        Cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => handleOpenEdit(item)}
                title="Edit Question"
              >
                <i className="bx bx-edit-alt me-1"></i> Edit
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => handleDeleteQuestion(item.question_id)}
                title="Delete Question"
              >
                <i className="bx bx-trash me-1"></i> Delete
              </button>
            </div>
          );
        },
      },
      {
        Header: "Status",
        Cell: ({ row }) => {
          const item = row.original;
          const isHidden = item.hidden === "yes";
          return (
            <button
              className={`btn btn-sm ${isHidden ? "btn-soft-danger" : "btn-soft-success"
                }`}
              onClick={() => handleToggleHide(item)}
              title={isHidden ? "Unhide" : "Hide"}
            >
              {isHidden ? (
                <span>
                  <i className="bx bx-hide me-1"></i> Hidden
                </span>
              ) : (
                <span>
                  <i className="bx bx-show me-1"></i> Visible
                </span>
              )}
            </button>
          );
        },
      },
    ],
    [selectedCourse, selectedUnit]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Question Bank" breadcrumbItem="Unit Questions" />

          {/* Filter Card */}
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm">
                <CardBody>
                  <h5 className="card-title mb-4 font-size-16 text-primary">
                    <i className="bx bx-filter-alt me-2"></i> Filter Questions
                  </h5>
                  <Row className="g-3 align-items-end">
                    {/* Course Filter */}
                    <Col md={5}>
                      <label className="form-label fw-semibold">
                        Select Course
                      </label>
                      <Select
                        placeholder="Search and select course..."
                        options={courses}
                        isLoading={loadingCourses}
                        value={selectedCourse}
                        onChange={(selected) => {
                          setSelectedCourse(selected);
                          if (selected) {
                            getUnits(selected.value);
                          } else {
                            setUnits([]);
                            setSelectedUnit(null);
                            setQuestions([]);
                          }
                        }}
                        isClearable
                      />
                    </Col>

                    {/* Unit Filter */}
                    <Col md={5}>
                      <label className="form-label fw-semibold">
                        Select Unit
                      </label>
                      <Select
                        placeholder={
                          !selectedCourse
                            ? "Please select course first..."
                            : "Search and select unit..."
                        }
                        options={units}
                        isLoading={loadingUnits}
                        value={selectedUnit}
                        isDisabled={!selectedCourse}
                        onChange={(selected) => {
                          setSelectedUnit(selected);
                          if (selected && selectedCourse) {
                            getQuestions(selected.value, selectedCourse.value);
                          } else {
                            setQuestions([]);
                          }
                        }}
                        isClearable
                      />
                    </Col>

                    {/* Add Question Button */}
                    <Col md={2} className="text-md-end">
                      <button
                        type="button"
                        className="btn btn-success w-100 d-flex align-items-center justify-content-center gap-1"
                        disabled={!selectedCourse || !selectedUnit}
                        onClick={() => setIsAddModalOpen(true)}
                      >
                        <i className="bx bx-plus font-size-18"></i>
                        <span>Add Question</span>
                      </button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Questions Table Card */}
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm">
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <h5 className="card-title font-size-16 mb-0">
                      Questions List{" "}
                      {selectedUnit ? `(Unit: ${selectedUnit.label})` : ""}
                    </h5>
                    <div className="d-flex gap-2 align-items-center">
                      {selectedUnit && (
                        <div className="position-relative" style={{ minWidth: "260px" }}>
                          <Input
                            type="text"
                            className="form-control form-control-sm pe-4"
                            placeholder="Search questions by text or ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <i
                            className="bx bx-search-alt position-absolute"
                            style={{
                              right: "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#74788d",
                            }}
                          ></i>
                        </div>
                      )}
                      {selectedUnit && (
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() =>
                            getQuestions(
                              selectedUnit.value,
                              selectedCourse?.value
                            )
                          }
                          title="Refresh Data"
                        >
                          <i className="bx bx-refresh me-1"></i> Refresh
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Top controls: Page Size Selector & Bulk Delete */}
                  {selectedUnit && (
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted font-size-13">Show:</span>
                        <select
                          className="form-select form-select-sm"
                          style={{ width: "80px" }}
                          value={pageSize}
                          onChange={handlePageSizeChange}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={30}>30</option>
                          <option value={50}>50</option>
                        </select>
                        <span className="text-muted font-size-13">entries</span>
                      </div>

                      {/* Bulk Delete Button when items are selected */}
                      {selectedQuestionIds.length > 0 && (
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-danger-subtle text-danger font-size-13 p-2">
                            {selectedQuestionIds.length} question(s) selected
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger d-flex align-items-center gap-1 shadow-sm"
                            onClick={handleBulkDelete}
                            disabled={isDeletingBulk}
                          >
                            {isDeletingBulk ? (
                              <Spinner size="sm" />
                            ) : (
                              <i className="bx bx-trash font-size-15"></i>
                            )}
                            Delete Selected ({selectedQuestionIds.length})
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {!selectedCourse ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-folder-open font-size-40 mb-2 d-block"></i>
                      <p className="font-size-16">
                        Please select a Course and a Unit to display questions.
                      </p>
                    </div>
                  ) : !selectedUnit ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-layer font-size-40 mb-2 d-block"></i>
                      <p className="font-size-16">
                        Please select a Unit to view its questions.
                      </p>
                    </div>
                  ) : loadingQuestions ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading questions...</p>
                    </div>
                  ) : questions.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-help-circle font-size-40 mb-2 d-block"></i>
                      <p className="font-size-16 mb-2">
                        No questions found in this unit.
                      </p>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => setIsAddModalOpen(true)}
                      >
                        <i className="bx bx-plus me-1"></i> Add First Question
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "40px" }} className="text-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input cursor-pointer"
                                  checked={
                                    questions.length > 0 &&
                                    selectedQuestionIds.length === questions.length
                                  }
                                  onChange={toggleSelectAllPage}
                                  title="Select/Deselect All on Page"
                                />
                              </th>
                              <th style={{ width: "50px" }}>#</th>
                              <th style={{ width: "120px" }}>Question ID</th>
                              <th>Question Title</th>
                              <th style={{ width: "100px" }}>Image</th>
                              <th>Answers</th>
                              <th style={{ width: "150px" }}>Actions</th>
                              <th style={{ width: "100px" }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {questions.map((item, idx) => {
                              const rowNumber = (currentPage - 1) * pageSize + idx + 1;
                              const answers = item.question_answers || [];
                              const isHidden = item.hidden === "yes";
                              const isSelected = selectedQuestionIds.includes(item.question_id);
                              return (
                                <tr
                                  key={item.question_id || idx}
                                  className={isSelected ? "table-active" : ""}
                                >
                                  <td className="text-center">
                                    <input
                                      type="checkbox"
                                      className="form-check-input cursor-pointer"
                                      checked={isSelected}
                                      onChange={() => toggleSelectQuestion(item.question_id)}
                                    />
                                  </td>
                                  <td><b>{rowNumber}</b></td>
                                  <td>{item.question_id}</td>
                                  <td>
                                    <div
                                      style={{
                                        maxWidth: "320px",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {item.question_text}
                                    </div>
                                  </td>
                                  <td>
                                    {item.question_image ? (
                                      <img
                                        src={item.question_image}
                                        alt="question"
                                        style={{
                                          width: "55px",
                                          height: "55px",
                                          objectFit: "cover",
                                          borderRadius: "6px",
                                          border: "1px solid #eee",
                                        }}
                                      />
                                    ) : (
                                      <span className="text-muted font-size-12">No Image</span>
                                    )}
                                  </td>
                                  <td>
                                    <ul
                                      style={{
                                        listStyleType: "none",
                                        padding: 0,
                                        margin: 0,
                                        textAlign: "left",
                                      }}
                                    >
                                      {answers.map((ans, aIdx) => (
                                        <li
                                          key={aIdx}
                                          style={{
                                            color: ans.answer_check ? "#28a745" : "#dc3545",
                                            fontWeight: ans.answer_check ? "600" : "normal",
                                            marginBottom: "3px",
                                          }}
                                        >
                                          {ans.answer_check ? "✓ " : "• "}
                                          {ans.answer_text}
                                        </li>
                                      ))}
                                    </ul>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={() => handleOpenEdit(item)}
                                        title="Edit Question"
                                      >
                                        <i className="bx bx-edit-alt me-1"></i> Edit
                                      </button>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleDeleteQuestion(item.question_id)}
                                        title="Delete Question"
                                      >
                                        <i className="bx bx-trash me-1"></i> Delete
                                      </button>
                                    </div>
                                  </td>
                                  <td>
                                    <button
                                      className={`btn btn-sm ${isHidden ? "btn-soft-danger" : "btn-soft-success"
                                        }`}
                                      onClick={() => handleToggleHide(item)}
                                      title={isHidden ? "Unhide" : "Hide"}
                                    >
                                      {isHidden ? (
                                        <span>
                                          <i className="bx bx-hide me-1"></i> Hidden
                                        </span>
                                      ) : (
                                        <span>
                                          <i className="bx bx-show me-1"></i> Visible
                                        </span>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Server Pagination Controls */}
                      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <div className="text-muted font-size-13">
                          Showing {(currentPage - 1) * pageSize + 1} to{" "}
                          {Math.min(currentPage * pageSize, paginationInfo.total_records || questions.length)}{" "}
                          of {paginationInfo.total_records || questions.length} entries
                        </div>
                        {paginationInfo.total_pages > 1 && (
                          <Pagination className="mb-0">
                            <PaginationItem disabled={currentPage <= 1}>
                              <PaginationLink
                                first
                                onClick={() => handlePageChange(1)}
                              />
                            </PaginationItem>
                            <PaginationItem disabled={currentPage <= 1}>
                              <PaginationLink
                                previous
                                onClick={() => handlePageChange(currentPage - 1)}
                              />
                            </PaginationItem>

                            {[...Array(paginationInfo.total_pages)].map((_, i) => {
                              const pageNum = i + 1;
                              if (
                                pageNum === 1 ||
                                pageNum === paginationInfo.total_pages ||
                                (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                              ) {
                                return (
                                  <PaginationItem
                                    active={pageNum === currentPage}
                                    key={pageNum}
                                  >
                                    <PaginationLink
                                      onClick={() => handlePageChange(pageNum)}
                                    >
                                      {pageNum}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              }
                              return null;
                            })}

                            <PaginationItem
                              disabled={currentPage >= paginationInfo.total_pages}
                            >
                              <PaginationLink
                                next
                                onClick={() => handlePageChange(currentPage + 1)}
                              />
                            </PaginationItem>
                            <PaginationItem
                              disabled={currentPage >= paginationInfo.total_pages}
                            >
                              <PaginationLink
                                last
                                onClick={() => handlePageChange(paginationInfo.total_pages)}
                              />
                            </PaginationItem>
                          </Pagination>
                        )}
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add Question Modal */}
      <Modal
        isOpen={isAddModalOpen}
        toggle={() => setIsAddModalOpen(!isAddModalOpen)}
        size="lg"
        centered
      >
        <ModalHeader
          toggle={() => setIsAddModalOpen(!isAddModalOpen)}
          className="bg-light"
        >
          <i className="bx bx-plus-circle me-1 text-success"></i> Add Question to {selectedUnit?.label}
        </ModalHeader>
        <ModalBody>
          {/* Tabs Selector: Create New vs Choose from Pool vs Import from Unit vs Import from Question Bank */}
          <div className="d-flex flex-wrap mb-4 border-bottom pb-2 gap-2">
            <button
              type="button"
              className={`btn btn-sm ${addModalTab === "new" ? "btn-primary" : "btn-light"}`}
              onClick={() => setAddModalTab("new")}
            >
              <i className="bx bx-pencil me-1"></i> Create New Question
            </button>
            <button
              type="button"
              className={`btn btn-sm ${addModalTab === "choose_pool" ? "btn-primary" : "btn-light"}`}
              onClick={() => {
                setAddModalTab("choose_pool");
                setSelectedPoolQuestionIds([]);
                getPoolQuestions("");
              }}
            >
              <i className="bx bx-select-multiple me-1"></i> Select Questions from Pool
            </button>
            <button
              type="button"
              className={`btn btn-sm ${addModalTab === "import_unit" ? "btn-primary" : "btn-light"}`}
              onClick={() => {
                setAddModalTab("import_unit");
                getUniversities();
                setImportUniversity(null);
                setImportGrade(null);
                setImportGradesList([]);
                setImportCourse(null);
                setImportUnit(null);
                setImportUnitsList([]);
                setImportUnitQuestions([]);
                setSelectedImportQuestionIds([]);
              }}
            >
              <i className="bx bx-import me-1"></i> Import from Course / Unit
            </button>
            <button
              type="button"
              className={`btn btn-sm ${addModalTab === "import_bank" ? "btn-primary" : "btn-light"}`}
              onClick={() => {
                setAddModalTab("import_bank");
                getQuestionBanksList();
                setSelectedImportBank(null);
                setImportBankQuestions([]);
                setSelectedImportBankQuestionIds([]);
              }}
            >
              <i className="bx bx-book-content me-1"></i> Import from Question Bank
            </button>
          </div>

          {addModalTab === "new" ? (
            <form onSubmit={handleAddQuestionSubmit}>
              {/* Question Text */}
              <div className="mb-3">
                <label className="form-label fw-bold">Question Title *</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Enter question title..."
                  value={addQuestionData.question_text}
                  onChange={(e) =>
                    setAddQuestionData({
                      ...addQuestionData,
                      question_text: e.target.value,
                    })
                  }
                  required
                ></textarea>
              </div>

              {/* Question Image */}
              <div className="mb-3">
                <label className="form-label fw-bold">Question Image (Optional)</label>
                <div className="d-flex gap-2 align-items-center">
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setAddImageFile(file);
                      if (file) handleUploadImage(file, false);
                    }}
                  />
                  {uploadingAddImg && <Spinner size="sm" color="primary" />}
                </div>
                {addQuestionData.question_image_url && (
                  <div className="mt-2">
                    <img
                      src={addQuestionData.question_image_url}
                      alt="preview"
                      style={{
                        maxHeight: "120px",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Answers Section */}
              <div className="mb-3 p-3 bg-light rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label fw-bold mb-0">
                    Answer Options (Select the correct answer using radio) *
                  </label>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() =>
                      setAddAnswersList([
                        ...addAnswersList,
                        {
                          id: addAnswersList.length + 1,
                          answer: "",
                          answer_exp: "",
                          checked: false,
                        },
                      ])
                    }
                  >
                    <i className="bx bx-plus me-1"></i> Add Option
                  </button>
                </div>

                {addAnswersList.map((item, index) => (
                  <div
                    key={index}
                    className="p-2 mb-2 bg-white rounded border d-flex flex-column gap-2"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="radio"
                        name="correct_answer_add"
                        checked={item.checked}
                        onChange={() => handleAddAnswerSelect(index)}
                        style={{ cursor: "pointer", transform: "scale(1.2)" }}
                        title="Mark as correct answer"
                      />
                      <Input
                        type="text"
                        placeholder={`Answer ${index + 1}`}
                        value={item.answer}
                        onChange={(e) =>
                          handleAddAnswerChange(index, "answer", e.target.value)
                        }
                        required
                      />
                      {addAnswersList.length > 2 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            const list = addAnswersList.filter(
                              (_, i) => i !== index
                            );
                            if (item.checked && list.length > 0) {
                              list[0].checked = true;
                            }
                            setAddAnswersList(list);
                          }}
                        >
                          <i className="bx bx-trash"></i>
                        </button>
                      )}
                    </div>
                    <Input
                      type="text"
                      placeholder={`Explanation for answer ${index + 1} (Optional)`}
                      value={item.answer_exp || ""}
                      onChange={(e) =>
                        handleAddAnswerChange(index, "answer_exp", e.target.value)
                      }
                      className="font-size-12 text-muted"
                    />
                  </div>
                ))}
              </div>

              {/* Explanation */}
              <div className="mb-3">
                <label className="form-label">General Explanation (Optional)</label>
                <textarea
                  className="form-control"
                  rows="2"
                  placeholder="Enter explanation for the correct answer..."
                  value={addQuestionData.explain || ""}
                  onChange={(e) =>
                    setAddQuestionData({
                      ...addQuestionData,
                      explain: e.target.value,
                    })
                  }
                ></textarea>
              </div>

              {/* Actions */}
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={submittingAdd || uploadingAddImg}
                >
                  {submittingAdd ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Saving...
                    </>
                  ) : (
                    "Save & Add to Unit"
                  )}
                </button>
              </div>
            </form>
          ) : addModalTab === "choose_pool" ? (
            <div>
              {/* Pool Search */}
              <div className="mb-3">
                <label className="form-label fw-bold">Search Questions Pool</label>
                <div className="d-flex gap-2">
                  <Input
                    type="text"
                    placeholder="Search by question title or ID..."
                    value={poolSearch}
                    onChange={(e) => setPoolSearch(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center gap-1"
                    onClick={() => getPoolQuestions(poolSearch)}
                  >
                    <i className="bx bx-search-alt"></i> Search
                  </button>
                </div>
              </div>

              {/* Questions Pool List with Checkbox Multi-Select */}
              {(() => {
                const existingIds = new Set();
                questions.forEach((q) => {
                  if (q.question_id) existingIds.add(String(q.question_id));
                  if (q.referenced_from) existingIds.add(String(q.referenced_from));
                });

                const availablePoolQuestions = poolQuestions.filter(
                  (pq) => !existingIds.has(String(pq.question_id))
                );

                return (
                  <>
                    {availablePoolQuestions.length > 0 ? (
                      <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                        <div className="form-check d-flex align-items-center gap-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="selectAllPool"
                            checked={
                              availablePoolQuestions.length > 0 &&
                              selectedPoolQuestionIds.length === availablePoolQuestions.length
                            }
                            onChange={() => toggleSelectAllPool(availablePoolQuestions)}
                            style={{ cursor: "pointer", transform: "scale(1.15)" }}
                          />
                          <label
                            className="form-check-label fw-bold text-primary mb-0"
                            htmlFor="selectAllPool"
                            style={{ cursor: "pointer" }}
                          >
                            Select All New ({availablePoolQuestions.length} Questions)
                          </label>
                        </div>
                        <span className="badge bg-soft-primary text-primary font-size-12">
                          {selectedPoolQuestionIds.length} Selected
                        </span>
                      </div>
                    ) : (
                      <div className="mb-2 px-1 text-success font-size-13 fw-semibold">
                        <i className="bx bx-check-double me-1"></i> All available pool questions are already added to this unit
                      </div>
                    )}

                    <div className="border rounded p-2 mb-3" style={{ maxHeight: "380px", overflowY: "auto" }}>
                      {loadingPool ? (
                        <div className="text-center py-4">
                          <Spinner color="primary" />
                          <p className="mt-2 text-muted">Loading questions from pool...</p>
                        </div>
                      ) : poolQuestions.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          <i className="bx bx-info-circle font-size-24 mb-1 d-block"></i>
                          No questions available in the question pool.
                        </div>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {poolQuestions.map((q) => {
                            const rootId = q.referenced_from || q.question_id;
                            const isAlreadyAdded = existingIds.has(String(rootId)) || existingIds.has(String(q.question_id));
                            const isSelected = selectedPoolQuestionIds.includes(q.question_id);
                            const answers = q.question_answers || [];

                            return (
                              <div
                                key={q.question_id}
                                className={`p-3 rounded border transition-all ${
                                  isAlreadyAdded
                                    ? "bg-light opacity-75 border-success-subtle"
                                    : isSelected
                                    ? "border-primary bg-soft-primary shadow-sm"
                                    : "bg-white"
                                }`}
                                style={{ cursor: isAlreadyAdded ? "default" : "pointer" }}
                                onClick={() => {
                                  if (!isAlreadyAdded) {
                                    togglePoolQuestionSelection(q.question_id);
                                  }
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-start gap-2">
                                  <div className="d-flex align-items-start gap-2">
                                    {isAlreadyAdded ? (
                                      <div
                                        className="d-flex align-items-center justify-content-center bg-success text-white rounded-circle mt-1"
                                        style={{ width: "20px", height: "20px", flexShrink: 0 }}
                                        title="Already Added to this Unit"
                                      >
                                        <i className="bx bx-check font-size-14"></i>
                                      </div>
                                    ) : (
                                      <input
                                        type="checkbox"
                                        className="form-check-input mt-1"
                                        checked={isSelected}
                                        onChange={() => togglePoolQuestionSelection(q.question_id)}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ transform: "scale(1.2)" }}
                                      />
                                    )}
                                    <div>
                                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                        <span className="fw-bold text-dark">
                                          #{q.question_id}: {q.question_text}
                                        </span>
                                        {isAlreadyAdded && (
                                          <span className="badge bg-success-subtle text-success border border-success font-size-11">
                                            <i className="bx bx-check me-1"></i> Already in Unit
                                          </span>
                                        )}
                                      </div>
                                      <div className="d-flex flex-wrap gap-2 font-size-12">
                                        {answers.map((a, aIdx) => (
                                          <span
                                            key={aIdx}
                                            className={`badge ${
                                              a.answer_check ? "bg-success" : "bg-secondary"
                                            }`}
                                          >
                                            {a.answer_text}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                  {q.question_image && (
                                    <img
                                      src={q.question_image}
                                      alt="preview"
                                      style={{ width: "45px", height: "45px", objectFit: "cover", borderRadius: "4px" }}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Assign button */}
                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setIsAddModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        disabled={selectedPoolQuestionIds.length === 0 || assigningFromPool}
                        onClick={handleAssignFromPool}
                      >
                        {assigningFromPool ? (
                          <>
                            <Spinner size="sm" className="me-1" /> Adding {selectedPoolQuestionIds.length} Question(s)...
                          </>
                        ) : (
                          <>
                            <i className="bx bx-check me-1"></i> Add Selected ({selectedPoolQuestionIds.length}) to Unit
                          </>
                        )}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          ) : addModalTab === "import_unit" ? (
            <div>
              {/* Import from Course / Unit with full hierarchy */}
              <div className="p-3 bg-light rounded mb-3 border">
                <h6 className="fw-bold text-primary mb-3">
                  <i className="bx bx-layer me-1"></i> Select Source Course & Unit to Import From:
                </h6>
                <Row className="g-3">
                  <Col md={6}>
                    <label className="form-label fw-bold font-size-13">University (Optional Filter)</label>
                    <Select
                      placeholder="All Universities / Select..."
                      options={universitiesList}
                      isLoading={loadingUniversities}
                      value={importUniversity}
                      onChange={(selected) => {
                        setImportUniversity(selected);
                        setImportGrade(null);
                        setImportCourse(null);
                        setImportUnit(null);
                        setImportUnitQuestions([]);
                        setSelectedImportQuestionIds([]);
                        if (selected && selected.original && Array.isArray(selected.original.grades)) {
                          setImportGradesList(
                            selected.original.grades.map((g) => ({
                              value: g.grade_id,
                              label: g.grade_name,
                            }))
                          );
                        } else {
                          setImportGradesList([]);
                        }
                      }}
                      isClearable
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-bold font-size-13">Grade (Optional Filter)</label>
                    <Select
                      placeholder={!importUniversity ? "Select university first..." : "Select grade..."}
                      options={importGradesList}
                      value={importGrade}
                      isDisabled={!importUniversity}
                      onChange={(selected) => {
                        setImportGrade(selected);
                        setImportCourse(null);
                        setImportUnit(null);
                        setImportUnitQuestions([]);
                        setSelectedImportQuestionIds([]);
                      }}
                      isClearable
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-bold font-size-13">Source Course *</label>
                    <Select
                      placeholder="Select course to import from..."
                      options={filteredImportCourses}
                      value={importCourse}
                      onChange={(selected) => {
                        setImportCourse(selected);
                        if (selected) {
                          getImportUnits(selected.value);
                        } else {
                          setImportUnitsList([]);
                          setImportUnit(null);
                          setImportUnitQuestions([]);
                        }
                      }}
                      isClearable
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-bold font-size-13">Source Unit *</label>
                    <Select
                      placeholder={!importCourse ? "Select course first..." : "Select unit..."}
                      options={importUnitsList}
                      isLoading={loadingImportUnits}
                      value={importUnit}
                      isDisabled={!importCourse}
                      onChange={(selected) => {
                        setImportUnit(selected);
                        if (selected && importCourse) {
                          getImportUnitQuestions(selected.value, importCourse.value);
                        } else {
                          setImportUnitQuestions([]);
                        }
                      }}
                      isClearable
                    />
                  </Col>
                </Row>
              </div>

              {/* Questions List from Selected Unit */}
              {importUnit && (
                <>
                  {(() => {
                    const existingIds = new Set();
                    questions.forEach((q) => {
                      if (q.question_id) existingIds.add(String(q.question_id));
                      if (q.referenced_from) existingIds.add(String(q.referenced_from));
                    });

                    const availableImportQuestions = importUnitQuestions.filter((iq) => {
                      const rootId = iq.referenced_from || iq.question_id;
                      return !existingIds.has(String(rootId)) && !existingIds.has(String(iq.question_id));
                    });

                    return (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                          {availableImportQuestions.length > 0 ? (
                            <div className="form-check d-flex align-items-center gap-2">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="selectAllImport"
                                checked={
                                  availableImportQuestions.length > 0 &&
                                  selectedImportQuestionIds.length === availableImportQuestions.length
                                }
                                onChange={() => toggleSelectAllImport(availableImportQuestions)}
                                style={{ cursor: "pointer", transform: "scale(1.15)" }}
                              />
                              <label
                                className="form-check-label fw-bold text-success mb-0"
                                htmlFor="selectAllImport"
                                style={{ cursor: "pointer" }}
                              >
                                Select All New ({availableImportQuestions.length} Questions)
                              </label>
                            </div>
                          ) : (
                            <span className="text-success font-size-13 fw-semibold">
                              <i className="bx bx-check-double me-1"></i> All questions from this unit are already added
                            </span>
                          )}
                          <span className="badge bg-soft-success text-success font-size-12">
                            {selectedImportQuestionIds.length} Selected to Import
                          </span>
                        </div>

                        <div className="border rounded p-2 mb-3" style={{ maxHeight: "360px", overflowY: "auto" }}>
                          {loadingImportQuestions ? (
                            <div className="text-center py-4">
                              <Spinner color="primary" />
                              <p className="mt-2 text-muted">Loading unit questions...</p>
                            </div>
                          ) : importUnitQuestions.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                              No questions found in this source unit.
                            </div>
                          ) : (
                            <div className="d-flex flex-column gap-2">
                              {importUnitQuestions.map((q) => {
                                const rootId = q.referenced_from || q.question_id;
                                const isAlreadyAdded = existingIds.has(String(rootId)) || existingIds.has(String(q.question_id));
                                const isSelected = selectedImportQuestionIds.includes(q.question_id);
                                const answers = q.question_answers || [];

                                return (
                                  <div
                                    key={q.question_id}
                                    className={`p-3 rounded border transition-all ${
                                      isAlreadyAdded
                                        ? "bg-light opacity-75 border-success-subtle"
                                        : isSelected
                                        ? "border-success bg-soft-success shadow-sm"
                                        : "bg-white"
                                    }`}
                                    style={{ cursor: isAlreadyAdded ? "default" : "pointer" }}
                                    onClick={() => {
                                      if (!isAlreadyAdded) {
                                        toggleImportQuestionSelection(q.question_id);
                                      }
                                    }}
                                  >
                                    <div className="d-flex justify-content-between align-items-start gap-2">
                                      <div className="d-flex align-items-start gap-2">
                                        {isAlreadyAdded ? (
                                          <div
                                            className="d-flex align-items-center justify-content-center bg-success text-white rounded-circle mt-1"
                                            style={{ width: "20px", height: "20px", flexShrink: 0 }}
                                            title="Already Added to this Unit"
                                          >
                                            <i className="bx bx-check font-size-14"></i>
                                          </div>
                                        ) : (
                                          <input
                                            type="checkbox"
                                            className="form-check-input mt-1"
                                            checked={isSelected}
                                            onChange={() => toggleImportQuestionSelection(q.question_id)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ transform: "scale(1.2)" }}
                                          />
                                        )}
                                        <div>
                                          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                            <span className="fw-bold text-dark">
                                              #{q.question_id}: {q.question_text}
                                            </span>
                                            {isAlreadyAdded && (
                                              <span className="badge bg-success-subtle text-success border border-success font-size-11">
                                                <i className="bx bx-check me-1"></i> Already in Unit
                                              </span>
                                            )}
                                          </div>
                                          <div className="d-flex flex-wrap gap-2 font-size-12">
                                            {answers.map((a, aIdx) => (
                                              <span
                                                key={aIdx}
                                                className={`badge ${
                                                  a.answer_check ? "bg-success" : "bg-secondary"
                                                }`}
                                              >
                                                {a.answer_text}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                      {q.question_image && (
                                        <img
                                          src={q.question_image}
                                          alt="preview"
                                          style={{ width: "45px", height: "45px", objectFit: "cover", borderRadius: "4px" }}
                                        />
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Import Action Buttons */}
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setIsAddModalOpen(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-success"
                            disabled={selectedImportQuestionIds.length === 0 || importingFromUnit}
                            onClick={handleImportFromUnit}
                          >
                            {importingFromUnit ? (
                              <>
                                <Spinner size="sm" className="me-1" /> Importing {selectedImportQuestionIds.length} Question(s)...
                              </>
                            ) : (
                              <>
                                <i className="bx bx-download me-1"></i> Import Selected ({selectedImportQuestionIds.length}) to Unit
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          ) : (
            <div>
              {/* Import from Question Bank */}
              <div className="p-3 bg-light rounded mb-3 border">
                <h6 className="fw-bold text-primary mb-3">
                  <i className="bx bx-book-content me-1"></i> Select Question Bank to Import From:
                </h6>
                <Row className="g-3">
                  <Col md={12}>
                    <label className="form-label fw-bold font-size-13">Source Question Bank *</label>
                    <Select
                      placeholder="Select Question Bank..."
                      options={questionBanksList}
                      isLoading={loadingQuestionBanks}
                      value={selectedImportBank}
                      onChange={(selected) => {
                        setSelectedImportBank(selected);
                        if (selected) {
                          getImportBankQuestions(selected.value);
                        } else {
                          setImportBankQuestions([]);
                          setSelectedImportBankQuestionIds([]);
                        }
                      }}
                      isClearable
                    />
                  </Col>
                </Row>
              </div>

              {/* Questions List from Selected Question Bank */}
              {selectedImportBank && (
                <>
                  {(() => {
                    const existingIds = new Set();
                    questions.forEach((q) => {
                      if (q.question_id) existingIds.add(String(q.question_id));
                      if (q.referenced_from) existingIds.add(String(q.referenced_from));
                    });

                    const availableImportQuestions = importBankQuestions.filter((iq) => {
                      const rootId = iq.referenced_from || iq.question_id;
                      return !existingIds.has(String(rootId)) && !existingIds.has(String(iq.question_id));
                    });

                    return (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                          {availableImportQuestions.length > 0 ? (
                            <div className="form-check d-flex align-items-center gap-2">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="selectAllBankImport"
                                checked={
                                  availableImportQuestions.length > 0 &&
                                  selectedImportBankQuestionIds.length === availableImportQuestions.length
                                }
                                onChange={() => toggleSelectAllBankImport(availableImportQuestions)}
                                style={{ cursor: "pointer", transform: "scale(1.15)" }}
                              />
                              <label
                                className="form-check-label fw-bold text-primary mb-0"
                                htmlFor="selectAllBankImport"
                                style={{ cursor: "pointer" }}
                              >
                                Select All New ({availableImportQuestions.length} Questions)
                              </label>
                            </div>
                          ) : (
                            <span className="text-success font-size-13 fw-semibold">
                              <i className="bx bx-check-double me-1"></i> All questions in this bank are already added to this unit
                            </span>
                          )}
                          <span className="badge bg-soft-primary text-primary font-size-12">
                            {selectedImportBankQuestionIds.length} Selected to Import
                          </span>
                        </div>

                        <div className="border rounded p-2 mb-3" style={{ maxHeight: "360px", overflowY: "auto" }}>
                          {loadingImportBankQuestions ? (
                            <div className="text-center py-4">
                              <Spinner color="primary" />
                              <p className="mt-2 text-muted">Loading questions from bank...</p>
                            </div>
                          ) : importBankQuestions.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                              No questions found in this question bank.
                            </div>
                          ) : (
                            <div className="d-flex flex-column gap-2">
                              {importBankQuestions.map((q) => {
                                const rootId = q.referenced_from || q.question_id;
                                const isAlreadyAdded = existingIds.has(String(rootId)) || existingIds.has(String(q.question_id));
                                const isSelected = selectedImportBankQuestionIds.includes(q.question_id);
                                const answers = q.question_answers || [];

                                return (
                                  <div
                                    key={q.question_id}
                                    className={`p-3 rounded border transition-all ${
                                      isAlreadyAdded
                                        ? "bg-light opacity-75 border-success-subtle"
                                        : isSelected
                                        ? "border-primary bg-soft-primary shadow-sm"
                                        : "bg-white"
                                    }`}
                                    style={{ cursor: isAlreadyAdded ? "default" : "pointer" }}
                                    onClick={() => {
                                      if (!isAlreadyAdded) {
                                        toggleImportBankQuestionSelection(q.question_id);
                                      }
                                    }}
                                  >
                                    <div className="d-flex justify-content-between align-items-start gap-2">
                                      <div className="d-flex align-items-start gap-2">
                                        {isAlreadyAdded ? (
                                          <div
                                            className="d-flex align-items-center justify-content-center bg-success text-white rounded-circle mt-1"
                                            style={{ width: "20px", height: "20px", flexShrink: 0 }}
                                            title="Already Added to this Unit"
                                          >
                                            <i className="bx bx-check font-size-14"></i>
                                          </div>
                                        ) : (
                                          <input
                                            type="checkbox"
                                            className="form-check-input mt-1"
                                            checked={isSelected}
                                            onChange={() => toggleImportBankQuestionSelection(q.question_id)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ transform: "scale(1.2)" }}
                                          />
                                        )}
                                        <div>
                                          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                            <span className="fw-bold text-dark">
                                              #{q.question_id}: {q.question_text}
                                            </span>
                                            {isAlreadyAdded && (
                                              <span className="badge bg-success-subtle text-success border border-success font-size-11">
                                                <i className="bx bx-check me-1"></i> Already in Unit
                                              </span>
                                            )}
                                          </div>
                                          <div className="d-flex flex-wrap gap-2 font-size-12">
                                            {answers.map((a, aIdx) => (
                                              <span
                                                key={aIdx}
                                                className={`badge ${
                                                  a.answer_check ? "bg-success" : "bg-secondary"
                                                }`}
                                              >
                                                {a.answer_text}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                      {q.question_image && (
                                        <img
                                          src={q.question_image}
                                          alt="preview"
                                          style={{ width: "45px", height: "45px", objectFit: "cover", borderRadius: "4px" }}
                                        />
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Import Action Buttons */}
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setIsAddModalOpen(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-success"
                            disabled={selectedImportBankQuestionIds.length === 0 || importingFromBank}
                            onClick={handleImportFromBank}
                          >
                            {importingFromBank ? (
                              <>
                                <Spinner size="sm" className="me-1" /> Importing {selectedImportBankQuestionIds.length} Question(s)...
                              </>
                            ) : (
                              <>
                                <i className="bx bx-download me-1"></i> Import Selected ({selectedImportBankQuestionIds.length}) to Unit
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Edit Question Modal */}
      <Modal
        isOpen={isEditModalOpen}
        toggle={() => setIsEditModalOpen(!isEditModalOpen)}
        size="lg"
        centered
      >
        <ModalHeader
          toggle={() => setIsEditModalOpen(!isEditModalOpen)}
          className="bg-light"
        >
          <i className="bx bx-edit text-primary me-1"></i> Edit Question #
          {editQuestionData.question_id}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleEditQuestionSubmit}>
            {/* Question Text */}
            <div className="mb-3">
              <label className="form-label fw-bold">Question Title *</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Enter question title..."
                value={editQuestionData.question_text || ""}
                onChange={(e) =>
                  setEditQuestionData({
                    ...editQuestionData,
                    question_text: e.target.value,
                  })
                }
                required
              ></textarea>
            </div>

            {/* Question Image */}
            <div className="mb-3">
              <label className="form-label fw-bold">Question Image (Optional)</label>
              <div className="d-flex gap-2 align-items-center">
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    setEditImageFile(file);
                    if (file) handleUploadImage(file, true);
                  }}
                />
                {uploadingEditImg && <Spinner size="sm" color="primary" />}
              </div>
              {editQuestionData.question_image_url && (
                <div className="mt-2 d-flex align-items-center gap-2">
                  <img
                    src={editQuestionData.question_image_url}
                    alt="preview"
                    style={{
                      maxHeight: "120px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() =>
                      setEditQuestionData({
                        ...editQuestionData,
                        question_image_url: "",
                      })
                    }
                  >
                    Remove Image
                  </button>
                </div>
              )}
            </div>

            {/* Answers Section */}
            <div className="mb-3 p-3 bg-light rounded">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label fw-bold mb-0">
                  Answer Options (Select the correct answer using radio) *
                </label>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() =>
                    setEditAnswersList([
                      ...editAnswersList,
                      {
                        id: editAnswersList.length + 1,
                        answer: "",
                        answer_exp: "",
                        checked: false,
                      },
                    ])
                  }
                >
                  <i className="bx bx-plus me-1"></i> Add Option
                </button>
              </div>

              {editAnswersList.map((item, index) => (
                <div
                  key={index}
                  className="p-2 mb-2 bg-white rounded border d-flex flex-column gap-2"
                >
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="radio"
                      name="correct_answer_edit"
                      checked={item.checked}
                      onChange={() => handleEditAnswerSelect(index)}
                      style={{ cursor: "pointer", transform: "scale(1.2)" }}
                      title="Mark as correct answer"
                    />
                    <Input
                      type="text"
                      placeholder={`Answer ${index + 1}`}
                      value={item.answer}
                      onChange={(e) =>
                        handleEditAnswerChange(index, "answer", e.target.value)
                      }
                      required
                    />
                    {editAnswersList.length > 2 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                          const list = editAnswersList.filter(
                            (_, i) => i !== index
                          );
                          if (item.checked && list.length > 0) {
                            list[0].checked = true;
                          }
                          setEditAnswersList(list);
                        }}
                      >
                        <i className="bx bx-trash"></i>
                      </button>
                    )}
                  </div>
                  <Input
                    type="text"
                    placeholder={`Explanation for answer ${index + 1} (Optional)`}
                    value={item.answer_exp || ""}
                    onChange={(e) =>
                      handleEditAnswerChange(
                        index,
                        "answer_exp",
                        e.target.value
                      )
                    }
                    className="font-size-12 text-muted"
                  />
                </div>
              ))}
            </div>

            {/* Explanation */}
            <div className="mb-3">
              <label className="form-label">General Explanation (Optional)</label>
              <textarea
                className="form-control"
                rows="2"
                placeholder="Enter explanation for the correct answer..."
                value={editQuestionData.explain || ""}
                onChange={(e) =>
                  setEditQuestionData({
                    ...editQuestionData,
                    explain: e.target.value,
                  })
                }
              ></textarea>
            </div>

            {/* Actions */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submittingEdit || uploadingEditImg}
              >
                {submittingEdit ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Updating...
                  </>
                ) : (
                  "Update Question"
                )}
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default QuestionBank;
